import React, { FormEvent, useState } from "react"
import nextId from "react-id-generator"

import useHistoryCustom from "../../hooks/useHistoryCustom"
import { IUser } from "../../interfaces/user"
import MessageError from "../message.error"
import UserEventApi from "../../services/api/UserEvent.api"
import { IFormCreate, IModel } from "../../interfaces/form"
import UserApi from "../../services/api/User.api"
import Input from "./input"
import TextArea from "./textarea"
import Select from "./Select"
import InputRadioCheckbox from "./input.radio.checkbox"
import Button from "./button"

interface IProps {
  user?: IUser
  company?: any
  backLink?: string
  backLabel?: string
  form?: IFormCreate | null
  afterSave?: () => void
}

const Form = ({
  user,
  company,
  backLabel,
  backLink,
  form,
  afterSave
}: IProps) => {
  const history = useHistoryCustom()

  const [activePage, setActivePage] = useState<number>(0)
  const [userState, setUser] = useState<any>(user || {})
  const [companyState, setCompany] = useState<any>(company || {})
  const [error, setError] = useState<string>("")

  const getStateFromModel = (model: IModel) => {
    if (model.entity === "user") return userState
    if (model.entity === "company") return companyState
    if (model.entity === "authentication") return userState.Authentication || {}
    if (model.entity === "userEvent") return userState.UserEvent || {}
    return null
  }

  const getSetStateFromModel = (model: IModel) => {
    if (model.entity === "user") return setUser
    if (model.entity === "company") return setCompany
    if (model.entity === "authentication")
      return (auth: any) =>
        setUser({
          ...userState,
          email: auth ? auth.email : undefined,
          password: auth ? auth.password : undefined,
          Authentication: auth
        })
    if (model.entity === "userEvent")
      return (UserEvent: any) =>
        setUser({
          ...userState,
          UserEvent
        })
    return null
  }

  const getValue = (model: IModel) => {
    const state = getStateFromModel(model)
    if (!state) return null

    if (model.field === "extended")
      return state && state.extended && state.extended[model.fieldExtended]
    return state && state[model.field]
  }

  const setValue = (value: string, model: IModel) => {
    const state = getStateFromModel(model)
    const setState = getSetStateFromModel(model)
    if (!state || !setState) return

    if (model.field === "extended")
      setState({
        ...state,
        extended: { ...state.extended, [model.fieldExtended]: value }
      })
    else setState({ ...state, [model.field]: value })
  }

  const getSelectValue = (model: IModel) => {
    const state = getStateFromModel(model)

    if (!state || !state.extended || !state.extended[model.fieldExtended])
      return ""

    const entries = Object.entries(state.extended[model.fieldExtended])
    const find = entries.find(e => e[1])
    if (find) return find[0]
    return ""
  }

  const setSelectValue = (value: string, model: IModel) => {
    const state = getStateFromModel(model)
    const setState = getSetStateFromModel(model)
    if (!state || !setState) return

    setState({
      ...state,
      extended: {
        ...state.extended,
        [model.fieldExtended]: model.values.reduce((obj: any, current) => {
          if (value === current.field) return { [current.field]: true }
          return obj
        }, undefined)
      }
    })
  }

  const getSelectedValues = (model: IModel) => {
    const state = getStateFromModel(model)

    const values: string[] = []

    if (!state || !state.extended || !state.extended[model.fieldExtended])
      return values
    const entries = Object.entries(state.extended[model.fieldExtended])
    entries.forEach(e => e[1] && values.push(e[0]))
    return values
  }

  const setSelectedValues = (value: string, model: IModel) => {
    const state = getStateFromModel(model)
    const setState = getSetStateFromModel(model)
    if (!state || !setState) return

    setState({
      ...state,
      extended: {
        ...state.extended,
        [model.fieldExtended]: model.values.reduce((obj: any, current) => {
          const actual =
            state.extended?.[model.fieldExtended]?.[current.field] || false

          if (current.field === value) {
            if (actual) return obj
            return { ...obj, [current.field]: true }
          }

          if (actual) return { ...obj, [current.field]: true }
          return obj
        }, {})
      }
    })
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    setError("")
    event.preventDefault()
    if (userState && userState.id) {
      await Promise.all([
        await new UserApi().update(userState.id, userState),
        await new UserEventApi().updateOrCreateOne(
          userState.id,
          userState.UserEvent
        )
      ])
        .then(() => afterSave && afterSave())
        .catch(err => setError(err.message))
    }
  }

  if (!form) return <div>Vous devez choisir un formulaire</div>

  if (!form.pages.length)
    return <div>Le formulaire doit contenir 1 page au minimum</div>

  return (
    <div>
      <div className="tabs is-medium">
        <ul>
          {form.pages.map((p, i) => (
            <li
              key={nextId("tab-top-")}
              className={`${i === activePage && "is-active"}`}
            >
              <a onClick={() => setActivePage(i)}>{p.label}</a>
            </li>
          ))}
        </ul>
      </div>
      <form id="form" onSubmit={onSubmit} className="field">
        {form.pages.map((p, i) => (
          <div
            key={`form-page-${i}`}
            id={`page-${i}`}
            className={`field ${i !== activePage && "is-hidden"}`}
          >
            {p.models.map((m, mi) => {
              if (m.component === "input")
                return (
                  <Input
                    key={`form-model-${mi}`}
                    value={getValue(m)}
                    onChange={value => setValue(value, m)}
                    type={m.type || "text"}
                    label={m.label}
                    required={m.required}
                    isHorizontal={!m.isVertical}
                  />
                )
              if (m.component === "textarea")
                return (
                  <TextArea
                    key={`form-model-${mi}`}
                    value={getValue(m)}
                    onChange={value => setValue(value, m)}
                    label={m.label}
                    required={m.required}
                    isHorizontal={!m.isVertical}
                  />
                )
              if (m.component === "select")
                return (
                  <Select
                    key={`form-model-${mi}`}
                    selectedValue={getSelectValue(m) || ""}
                    options={(m.values || []).map(v => ({
                      ...v,
                      value: v.field
                    }))}
                    label={m.label}
                    setSelectedValue={value => setSelectValue(value, m)}
                    required={m.required}
                    isHorizontal={!m.isVertical}
                    choiceNoValue
                    hideDefault
                  />
                )
              if (m.component === "radio")
                return (
                  <InputRadioCheckbox
                    key={`form-model-${mi}`}
                    options={(m.values || []).map(v => ({
                      ...v,
                      value: v.field
                    }))}
                    label={m.label}
                    type={m.component}
                    required={m.required}
                    selectedValues={[getSelectValue(m)]}
                    setSelectedValue={value => setSelectValue(value, m)}
                    isHorizontal={!m.isVertical}
                  />
                )
              if (m.component === "checkbox")
                return (
                  <InputRadioCheckbox
                    key={`form-model-${mi}`}
                    options={(m.values || []).map(v => ({
                      ...v,
                      value: v.field
                    }))}
                    label={m.label}
                    type={m.component}
                    required={m.required}
                    selectedValues={getSelectedValues(m)}
                    setSelectedValue={value => setSelectedValues(value, m)}
                    isHorizontal={!m.isVertical}
                  />
                )
              return ""
            })}
          </div>
        ))}

        {error && <MessageError message={error} />}

        <div className="field is-grouped is-grouped-centered">
          {backLink && backLabel && (
            <p className="control">
              <Button
                label={backLabel}
                onClick={() => history.push(backLink)}
                type="dark"
              />
            </p>
          )}
          <p className="control">
            <Button
              label="Enregistrer"
              onClick={() => {
                for (let i = 0; i < form.pages.length; i++) {
                  const pageElement = document.getElementById(`page-${i}`)
                  if (!pageElement) return null

                  const inputRequired = pageElement.querySelectorAll(
                    "[required]"
                  )
                  for (let j = 0; j < inputRequired.length; j++) {
                    const input = inputRequired[j] as HTMLFormElement
                    if (!input.checkValidity()) return setActivePage(i)
                  }
                }
              }}
              submit
            />
          </p>
        </div>
      </form>

      <div className="tabs is-medium">
        <ul>
          {form.pages.map((p, i) => (
            <li
              key={nextId("tab-bottom-")}
              className={`${i === activePage && "is-active"}`}
            >
              <a onClick={() => setActivePage(i)}>{p.label}</a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default Form
